import { useEffect } from "react";
import { BrowserRouter, Route, Switch } from "react-router-dom";

import { Can } from "components";
import { PrivateRouteLayout, PublicRouteLayout } from "layouts";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import {
  AccessKeysDashboard,
  AccountSettings,
  AcsesLogsDashboard,
  AgreementsDashboard,
  AppointmentDetails,
  Appointments,
  BackordersDashboard,
  BrandsDashboard,
  CarDetails,
  CheckInServices,
  ChecklistSettingsDashboard,
  ChecklistsDashboard,
  CommunicationSettings,
  CommunicationTemplates,
  CustomQueries,
  CustomersDashboard,
  DMSCapabilities,
  Monitoring as DMSv3MonitoringDashboard,
  Dayplanner,
  DealerDetails,
  DealerDocumentations,
  DealerLicenseOverview,
  DealerLocationDetails,
  DealerOverviewDashboard,
  DealerReports,
  DealersDashboard,
  DevicesDashboard,
  Settings as DmsSettings,
  DpoContract,
  ExactMonitoring,
  FactoryExportsDashboard,
  HoldingAppointmentDetails,
  HoldingsDashboard,
  IncompleteTyresDashboard,
  KeyLockersDashboard,
  KioskDashboard,
  LeadsDashboard,
  LocationLicenseOverview,
  LocationOverviewDashboard,
  LocationReports,
  Login,
  ManufacturerDashboard,
  NewsDashboard,
  NoteSuggestions,
  ParkingGateCodesDashboard,
  PendingChecklistsDashboard,
  PrivacyAndSecurityDashboard,
  QuestionsDashboard,
  ReportTemplatesDashboard,
  ShareboxDashboard,
  StatusListDashboard,
  Styleguide,
  TagsDashboard,
  ThreeDepartmentDashboard,
  TyreDiscountsDashboard,
  TyresDashboard,
  UnlinkedExactLicensesDashboard,
  UsersDashboard,
  WarrantyDashboard,
  WebhooksDashboard
} from "modules";
import { setZohoDeskASAP } from "plugins/ZohoDeskASAP";
import { Axios401Interceptor, BroadcastChannelManager, NotFoundPage } from "router/components";
import { useStoreRedirectUrlParam } from "router/hooks";
import { PATHS } from "router/paths";

// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import packageJson from "../../package.json";

export const Router = () => {
  useEffect(() => {
    document.title = `Claire V${packageJson.version}`;
    setZohoDeskASAP();
  }, []);

  useStoreRedirectUrlParam();

  return (
    <BrowserRouter>
      <BroadcastChannelManager>
        <Switch>
          <Route exact path={[PATHS.HOME, PATHS.LOGIN, PATHS.RESET_PASSWORD, PATHS.RESET_PASSWORD_REQUEST]}>
            <PublicRouteLayout>
              <Login />
            </PublicRouteLayout>
          </Route>

          <Route
            exact
            path={PATHS.DPO_CONTRACT}
            render={() => (
              <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.Agreements} renderNoAccess>
                <DpoContract />
              </Can>
            )}
          />

          <Route
            exact
            path={[
              PATHS.ACCESS_KEYS_DASHBOARD,
              PATHS.ACCOUNT,
              PATHS.ACSES_LOGS_DASHBOARD,
              PATHS.AGREEMENTS_DASHBOARD,
              PATHS.APPOINTMENTS,
              PATHS.APPOINTMENT_DETAIL,
              PATHS.APPOINTMENT_DETAIL_LOCATION_ID_WO_NR,
              PATHS.BACKORDERS_DASHBOARD,
              PATHS.BRANDS_DASHBOARD,
              PATHS.CAR_DETAILS,
              PATHS.CHECKLIST_SETTINGS_DASHBOARD,
              PATHS.CHECKLISTS_DASHBOARD,
              PATHS.CHECK_IN_SERVICES_DASHBOARD,
              PATHS.COMMUNICATION_TEMPLATES,
              PATHS.CUSTOMERS_DASHBOARD,
              PATHS.CUSTOMER_COM,
              PATHS.DAYPLANNER,
              PATHS.DEALER_DOCUMENTATION,
              PATHS.DEALER_LICENSE_OVERVIEW,
              PATHS.DEALER_OVERVIEW_DASHBOARD,
              PATHS.DEALER_REPORTS,
              PATHS.DEALERS_DASHBOARD,
              PATHS.DEALERS_DASHBOARD_DEALER,
              PATHS.DEALERS_DASHBOARD_LOCATION,
              PATHS.DEVICES_DASHBOARD,
              PATHS.DMS_CAPABILITIES_DASHBOARD,
              PATHS.DMSV3_CUSTOM_QUERIES,
              PATHS.DMSV3_SETTINGS,
              PATHS.DMSv3_MONITORING_DASHBOARD,
              PATHS.INCOMPLETE_TYRES_DASHBOARD,
              PATHS.KEYLOCKERS_DASHBOARD,
              PATHS.KIOSK_DASHBOARD,
              PATHS.EXACT_MONITORING,
              PATHS.FACTORY_EXPORTS_DASHBOARD,
              PATHS.HOLDING_APPOINTMENT_DETAIL,
              PATHS.HOLDINGS_DASHBOARD,
              PATHS.LEADS_DASHBOARD,
              PATHS.LOCATION_LICENSE_OVERVIEW,
              PATHS.LOCATION_OVERVIEW_DASHBOARD,
              PATHS.LOCATION_REPORTS,
              PATHS.MANUFACTURER_DASHBOARD,
              PATHS.NEWS_DASHBOARD,
              PATHS.NOTES_MENU,
              PATHS.PARKING_GATE_CODES_DASHBOARD,
              PATHS.PENDING_CHECKLISTS_DASHBOARD,
              PATHS.PRIVACY_AND_SECURITY_DASHBOARD,
              PATHS.QUESTIONS_DASHBOARD,
              PATHS.REPORT_TEMPLATES_DASHBOARD,
              PATHS.SHAREBOX_DASHBOARD,
              PATHS.STATUS_LIST_DASHBOARD,
              PATHS.STYLE_GUIDE,
              PATHS.TAGS_DASHBOARD,
              PATHS.TYRES_DASHBOARD,
              PATHS.THREE_DEPARTMENT_DASHBOARD,
              PATHS.TYRE_DISCOUNTS_DASHBOARD,
              PATHS.UNLINKED_EXACT_LICENSES,
              PATHS.USERS,
              PATHS.WARRANTY_DASHBOARD,
              PATHS.WEBHOOKS_DASHBOARD
            ]}
          >
            <Axios401Interceptor>
              <PrivateRouteLayout>
                <Switch>
                  <Route
                    exact
                    path={PATHS.ACCESS_KEYS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="access-keys-page" renderNoAccess>
                        <AccessKeysDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.ACCOUNT}
                    render={() => (
                      <Can I="browse" the="account-page" renderNoAccess>
                        <AccountSettings />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.ACSES_LOGS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="acses-events-page" renderNoAccess>
                        <AcsesLogsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.AGREEMENTS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="check-in-agreements-page" renderNoAccess>
                        <AgreementsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.APPOINTMENTS}
                    render={() => (
                      <Can I="browse" the="appointments-page" renderNoAccess>
                        <Appointments />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.APPOINTMENT_DETAIL}
                    render={() => (
                      <Can I="browse" the="appointments-details-page" renderNoAccess>
                        <AppointmentDetails />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.APPOINTMENT_DETAIL_LOCATION_ID_WO_NR}
                    render={() => (
                      <Can I="browse" the="appointments-details-page" renderNoAccess>
                        <AppointmentDetails />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.BACKORDERS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="backorders-dashboard-page" renderNoAccess>
                        <BackordersDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.BRANDS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="brands-page" renderNoAccess>
                        <BrandsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.CAR_DETAILS}
                    render={() => (
                      <Can I="browse" the="appointments-car-page" renderNoAccess>
                        <CarDetails />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.CHECKLIST_SETTINGS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="checklist-settings-page" renderNoAccess>
                        <ChecklistSettingsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.CHECKLISTS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="checklists-page" renderNoAccess>
                        <ChecklistsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.CHECK_IN_SERVICES_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="check-in-service-page" renderNoAccess>
                        <CheckInServices />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.COMMUNICATION_TEMPLATES}
                    render={() => (
                      <Can I="browse" the="customer-comm-mediums-page" renderNoAccess>
                        <CommunicationTemplates />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.CUSTOMERS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="customers-page" renderNoAccess>
                        <CustomersDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.CUSTOMER_COM}
                    render={() => (
                      <Can I="browse" the="customer-comm-settings-page" renderNoAccess>
                        <CommunicationSettings />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DAYPLANNER}
                    render={() => (
                      <Can I="browse" the="dayplanner-page" renderNoAccess>
                        <Dayplanner />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEALER_DOCUMENTATION}
                    render={() => (
                      <Can I="browse" the="dealer-documentation-page" renderNoAccess>
                        <DealerDocumentations />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEALER_LICENSE_OVERVIEW}
                    render={() => (
                      <Can I="browse" the="exact-dealer-license-page" renderNoAccess>
                        <DealerLicenseOverview />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEALER_OVERVIEW_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="dealers-page" renderNoAccess>
                        <DealerOverviewDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEALER_REPORTS}
                    render={() => (
                      <Can I="browse" the="reports-page" renderNoAccess>
                        <DealerReports />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEVICES_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="devices-page" renderNoAccess>
                        <DevicesDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DMS_CAPABILITIES_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="dms-capabilities-page" renderNoAccess>
                        <DMSCapabilities />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DMSV3_CUSTOM_QUERIES}
                    render={() => (
                      <Can I="browse" the="dmsv3-customquery-page" renderNoAccess>
                        <CustomQueries />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DMSV3_SETTINGS}
                    render={() => (
                      <Can I={ACL_ACTIVITIES.Browse} the={ACL_RESOURCES.DMSv3SettingPage} renderNoAccess>
                        <DmsSettings />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DMSv3_MONITORING_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="dmsv3-monitoring-page" renderNoAccess>
                        <DMSv3MonitoringDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.EXACT_MONITORING}
                    render={() => (
                      <Can I="browse" the="exact-monitoring-page" renderNoAccess>
                        <ExactMonitoring />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.FACTORY_EXPORTS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="factory-export-page" renderNoAccess>
                        <FactoryExportsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEALERS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="admin-dealers-page" renderNoAccess>
                        <DealersDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEALERS_DASHBOARD_DEALER}
                    render={() => (
                      <Can I="browse" the="admin-dealers-page" renderNoAccess>
                        <DealerDetails />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.DEALERS_DASHBOARD_LOCATION}
                    render={() => (
                      <Can I="browse" the="admin-dealers-page" renderNoAccess>
                        <DealerLocationDetails />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.HOLDINGS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="holding" renderNoAccess>
                        <HoldingsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.HOLDING_APPOINTMENT_DETAIL}
                    render={() => (
                      <Can I="browse" the="appointments-details-page" renderNoAccess>
                        <HoldingAppointmentDetails />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.INCOMPLETE_TYRES_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="tires-upload-page" renderNoAccess>
                        <IncompleteTyresDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.KEYLOCKERS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="keylocker-page" renderNoAccess>
                        <KeyLockersDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.KIOSK_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="kiosk-page" renderNoAccess>
                        <KioskDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.LEADS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="leads-dashboard-page" renderNoAccess>
                        <LeadsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.LOCATION_LICENSE_OVERVIEW}
                    render={() => (
                      <Can I="browse" the="exact-license-page" renderNoAccess>
                        <LocationLicenseOverview />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.LOCATION_OVERVIEW_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="locations-page" renderNoAccess>
                        <LocationOverviewDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.LOCATION_REPORTS}
                    render={() => (
                      <Can I="browse" the="locations-reports-page" renderNoAccess>
                        <LocationReports />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.MANUFACTURER_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="manufacturer-page" renderNoAccess>
                        <ManufacturerDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.NEWS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="news-page" renderNoAccess>
                        <NewsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.NOTES_MENU}
                    render={() => (
                      <Can I="browse" the="note-comment-suggestions-page" renderNoAccess>
                        <NoteSuggestions />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.PARKING_GATE_CODES_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="parking-gate-code-page" renderNoAccess>
                        <ParkingGateCodesDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.PENDING_CHECKLISTS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="pending-checklist-page" renderNoAccess>
                        <PendingChecklistsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.PRIVACY_AND_SECURITY_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="privacy-security-page" renderNoAccess>
                        <PrivacyAndSecurityDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.QUESTIONS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="questions-page" renderNoAccess>
                        <QuestionsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.REPORT_TEMPLATES_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="templates-page" renderNoAccess>
                        <ReportTemplatesDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.SHAREBOX_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="sharebox-logs-page" renderNoAccess>
                        <ShareboxDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.STATUS_LIST_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="status-list-global-page" renderNoAccess>
                        <StatusListDashboard />
                      </Can>
                    )}
                  />
                  <Route exact path={PATHS.STYLE_GUIDE} component={Styleguide} />
                  <Route
                    exact
                    path={PATHS.THREE_DEPARTMENT_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="appointments-three-departments-page" renderNoAccess>
                        <ThreeDepartmentDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.TYRES_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="tyres-page" renderNoAccess>
                        <TyresDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.TYRE_DISCOUNTS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="tyre-discount-page" renderNoAccess>
                        <TyreDiscountsDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.UNLINKED_EXACT_LICENSES}
                    render={() => (
                      <Can I="browse" the="exact-unlinked-licenses-page" renderNoAccess>
                        <UnlinkedExactLicensesDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.USERS}
                    render={() => (
                      <Can I="browse" the="users-page" renderNoAccess>
                        <UsersDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.WARRANTY_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="warranty-dashboard-page" renderNoAccess>
                        <WarrantyDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.WEBHOOKS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="webhooks-page" renderNoAccess>
                        <WebhooksDashboard />
                      </Can>
                    )}
                  />
                  <Route
                    exact
                    path={PATHS.TAGS_DASHBOARD}
                    render={() => (
                      <Can I="browse" the="tags-page" renderNoAccess>
                        <TagsDashboard />
                      </Can>
                    )}
                  />
                </Switch>
              </PrivateRouteLayout>
            </Axios401Interceptor>
          </Route>

          <Route path="*" component={NotFoundPage} />
        </Switch>
      </BroadcastChannelManager>
    </BrowserRouter>
  );
};
